/** The source a requirement group draws its eligible items from. */
function groupSource(group: CatalogBundleGroup): BundleSource {
  return group.source ?? group.kind ?? 'items';
}

/** The item ids a group can draw from; the server resolves these for the till. */
function groupItemIds(group: CatalogBundleGroup): number[] {
  return Array.isArray(group.item_ids) ? group.item_ids : [];
}

/**
 * Find the dearest feasible b-matching for one bundle application.
 *
 * Each group is a demand for `quantity` units (a slot); each cart item is a
 * supply of units; an item may fill a slot only when it is eligible for that
 * group. Repeats are always allowed, so one item can fill several slots.
 *
 * The item units are walked dearest-first (id breaks price ties) and each is
 * added when an augmenting path shows it still fits. That is the greedy over
 * the transversal matroid, so the result is a maximum-weight assignment and is
 * deterministic; it mirrors `backend/app/bundles.py` exactly, so the till and
 * the server agree on which units a bundle consumes. Returns one item-id list
 * per group, or null when some slot cannot be filled.
 */
function matchGroups(groups: CatalogBundleGroup[], remaining: Map<number, number>, byId: Map<number, SelectedItem>): number[][] | null {
  // One slot per demanded unit, grouped in order.
  const slots: number[] = [];
  for (let index = 0; index < groups.length; index += 1) {
    for (let unit = 0; unit < groups[index].quantity; unit += 1) slots.push(index);
  }
  const totalUnits = slots.length;
  if (totalUnits === 0) return groups.map(() => []);

  const eligible = groups.map((group) => new Set(groupItemIds(group)));

  // Every unspent unit, dearest first.
  const units: number[] = [];
  for (const [itemId, count] of remaining) {
    for (let index = 0; index < count; index += 1) units.push(itemId);
  }
  units.sort((a, b) => (byId.get(b)?.price ?? 0) - (byId.get(a)?.price ?? 0) || a - b);

  const occupant: (number | null)[] = new Array(totalUnits).fill(null);
  let matched = 0;

  const augment = (itemId: number, visited: Set<number>): boolean => {
    for (let slot = 0; slot < totalUnits; slot += 1) {
      if (visited.has(slot)) continue;
      if (!eligible[slots[slot]].has(itemId)) continue;
      visited.add(slot);
      const current = occupant[slot];
      if (current === null || augment(current, visited)) {
        occupant[slot] = itemId;
        return true;
      }
    }
    return false;
  };

  for (const itemId of units) {
    if (augment(itemId, new Set<number>())) {
      matched += 1;
      if (matched === totalUnits) break;
    }
  }
  if (matched < totalUnits) return null;

  const chosen: number[][] = groups.map(() => []);
  for (let slot = 0; slot < totalUnits; slot += 1) {
    const itemId = occupant[slot];
    if (itemId !== null) chosen[slots[slot]].push(itemId);
  }
  return chosen;
}

/**
 * Work out which bundles a cart has earned, automatically.
 *
 * A bundle is a discount, not a line. It is earned when every requirement
 * group can be filled from the unspent cart units, using the dearest feasible
 * units. Selection is greedy: repeated passes take the bundle with the largest
 * saving that still fits (lowest id on a tie), then consume those units and
 * recompute, so no unit is ever spent twice.
 */
export function applyBundles(items: SelectedItem[], catalog: CatalogBundle[]): { bundles: AppliedBundle[]; savings: number } {
  const remaining = new Map<number, number>();
  const byId = new Map<number, SelectedItem>();
  for (const item of items) {
    remaining.set(item.id, (remaining.get(item.id) ?? 0) + item.quantity);
    byId.set(item.id, item);
  }

  const applied = new Map<number, AppliedBundle>();

  for (;;) {
    let best: { bundle: CatalogBundle; assignment: number[][]; saving: number } | null = null;

    for (const bundle of catalog) {
      const groups = Array.isArray(bundle.groups) ? bundle.groups : [];
      if (groups.length === 0) continue;

      const assignment = matchGroups(groups, remaining, byId);
      if (assignment === null) continue;

      let chosen = 0;
      for (const ids of assignment) {
        for (const id of ids) chosen += byId.get(id)?.price ?? 0;
      }
      const saving = chosen - bundle.price;
      // A bundle that costs at least as much as the parts is not a discount.
      if (saving <= 0) continue;

      if (best === null || saving > best.saving || (saving === best.saving && bundle.id < best.bundle.id)) {
        best = { bundle, assignment, saving };
      }
    }
    if (best === null) break;

    for (const ids of best.assignment) {
      for (const id of ids) {
        remaining.set(id, (remaining.get(id) ?? 0) - 1);
      }
    }

    const groups: AppliedBundleGroup[] = best.bundle.groups.map((group, index) => ({
      kind: groupSource(group),
      quantity: group.quantity,
      category_id: group.category_id ?? null,
      tag_id: group.tag_id ?? null,
      item_ids: [...best.assignment[index]],
    }));

    const existing = applied.get(best.bundle.id);
    if (existing) {
      existing.quantity += 1;
      existing.saving += best.saving;
      existing.groups.forEach((group, index) => group.item_ids.push(...groups[index].item_ids));
    } else {
      applied.set(best.bundle.id, {
        bundleId: best.bundle.id,
        title: best.bundle.name,
        price: best.bundle.price,
        quantity: 1,
        saving: best.saving,
        groups,
      });
    }
  }

  const bundles = [...applied.values()];
  const savings = bundles.reduce((sum, bundle) => sum + bundle.saving, 0);
  return { bundles, savings };
}

export const BUNDLE_CACHE_KEY = 'cachedBundles';

/** A bundle as an older client cached it, before requirement groups. */
type LegacyCachedComponent = {
  kind?: 'item' | 'tag';
  item_id?: number | null;
  tag_id?: number | null;
  quantity?: number;
  item_ids?: number[];
};

/**
 * Turn one cached entry into the current shape, or null when it is unusable.
 *
 * A cache written before the requirement-group redesign stored a bundle as a
 * flat component list. Fold that back into a single group per component so an
 * offline till that has not refetched yet keeps applying its discounts.
 */
function toCatalogBundle(value: unknown): CatalogBundle | null {
  if (value === null || typeof value !== 'object') return null;
  const bundle = value as Partial<CatalogBundle> & { items?: LegacyCachedComponent[] };
  if (typeof bundle.id !== 'number' || typeof bundle.name !== 'string' || typeof bundle.price !== 'number') {
    return null;
  }

  if (Array.isArray(bundle.groups)) {
    return { id: bundle.id, name: bundle.name, price: bundle.price, groups: bundle.groups };
  }

  if (!Array.isArray(bundle.items)) return null;
  const groups: CatalogBundleGroup[] = [];
  for (const component of bundle.items) {
    const tagId = component.kind === 'tag' ? (component.tag_id ?? null) : null;
    const itemIds = component.kind === 'tag' ? (component.item_ids ?? []) : component.item_id != null ? [component.item_id] : [];
    if (itemIds.length === 0) continue;
    groups.push({
      kind: component.kind === 'tag' ? 'tag' : 'items',
      quantity: component.quantity ?? 1,
      category_id: null,
      tag_id: tagId,
      item_ids: itemIds,
    });
  }
  return { id: bundle.id, name: bundle.name, price: bundle.price, groups };
}

/**
 * Bundles saved from the last successful fetch.
 *
 * The cart derives its discounts from this list, so it must survive an offline
 * reload: without it, a till that restarts with no connection would stop
 * applying a bundle the operator can still see on screen.
 */
export function readCachedBundles(): CatalogBundle[] {
  try {
    const raw = localStorage.getItem(BUNDLE_CACHE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map(toCatalogBundle).filter((bundle): bundle is CatalogBundle => bundle !== null);
  } catch {
    return [];
  }
}

/** Remember the latest bundle list so an offline reload can still apply it. */
export function cacheBundles(bundles: CatalogBundle[]): void {
  try {
    localStorage.setItem(BUNDLE_CACHE_KEY, JSON.stringify(bundles));
  } catch {
    // Storage unavailable; the discount still applies for this session.
  }
}
